import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { WorkflowEditSidebar } from "@/pages/workflows/edit/components/WorkflowEditSidebar.tsx";
import {
  useWorkflowEditPage,
  WorkflowEditPageProps,
  WorkflowEditPageStep,
} from "@/pages/workflows/edit/pages/workflow-edit-pages.tsx";
import { WorkflowTitleSlot } from "@/pages/workflows/edit/pages/WorkflowTitleSlot.tsx";
import { useWorkflowUpdateDetails } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import {
  Avatar,
  Box,
  Button,
  FontAwesomeIcon,
  FullScreenPage,
  Input,
  ListItem,
  Stack,
  Switch,
  Text,
  Textarea,
  Title,
} from "@skailer-com/uikit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface VisibilityFormValues {
  is_shared: boolean;
  emoji: string | null;
  public_title: string;
  public_description: string;
}

export const WorkflowEditVisibilityPage = ({
  workflow,
  workflowVersion,
}: WorkflowEditPageProps) => {
  const { t } = useTranslation();
  const { confirmExit, navigateToStep } = useWorkflowEditPage();

  useTitle(t("workflow:configure_workflow"), t("workflow:workflows"));

  const workflowUpdateDetails = useWorkflowUpdateDetails();
  const workflowEmoji = workflow.emoji ?? emojiDefaults.workflows.default;

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { isDirty },
  } = useForm<VisibilityFormValues>({
    values: {
      is_shared: workflow.is_shared,
      emoji: workflowEmoji,
      public_title: workflow.public_title ?? "",
      public_description: workflow.public_description ?? "",
    },
  });

  const isShared = watch("is_shared");
  const previewEmoji = watch("emoji");
  const previewTitle = watch("public_title");
  const previewDescription = watch("public_description");

  const handleSave = handleSubmit(async (data) => {
    if (!isDirty && workflow.emoji) {
      navigateToStep(workflow.id, WorkflowEditPageStep.automation);
      return;
    }
    try {
      await workflowUpdateDetails.mutateAsync({
        workflowId: workflow.id,
        workflowVersionId: workflow.current_version_id,
        payload: {
          title: workflow.title,
          description: workflow.description,
          folder_id: workflow.folder.id,
          emoji: data.emoji,
          public_title: data.public_title || null,
          public_description: data.public_description || null,
          is_shared: data.is_shared,
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.edited, {
        [analyticProperties.id]: workflow.id,
        [analyticProperties.tab]: WorkflowEditPageStep.visibility,
      });

      navigateToStep(workflow.id, WorkflowEditPageStep.automation);
    } catch {
      //  handle error
    }
  });

  return (
    <FullScreenPage
      isCenterVertical={false}
      titleSlot={
        <WorkflowTitleSlot
          step={WorkflowEditPageStep.visibility}
          workflow={workflow}
          workflowVersion={workflowVersion}
        />
      }
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit()}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <Stack direction={"horizontal"} gap={"sm"}>
          <Button
            variant={"secondary"}
            size={"lg"}
            onClick={() => navigateToStep(workflow.id, WorkflowEditPageStep.details)}
          >
            {t("common:back")}
          </Button>
          <Button size={"lg"} onClick={handleSave} isLoading={workflowUpdateDetails.isPending}>
            {t("common:continue")}
          </Button>
        </Stack>
      }
      sidebarSlot={
        <WorkflowEditSidebar
          step={WorkflowEditPageStep.visibility}
          workflow={workflow}
          workflowVersion={workflowVersion}
        />
      }
    >
      <form onSubmit={handleSave}>
        <Stack gap={"sm"} justify={"center"}>
          <Stack className={"card-panel"} gap={"lg"}>
            <Stack gap={"xs"}>
              <Title header={t("common:visibility")} size={"md"} />
              <Text className={"text-secondary"}>{t("workflow:visibility_description")}</Text>
            </Stack>
            <Controller
              control={control}
              name={"is_shared"}
              render={({ field: { value, onChange } }) => (
                <Switch
                  checked={value}
                  onChange={onChange}
                  title={t("workflow:visibility_make_available")}
                  description={t("workflow:visibility_make_available_description")}
                />
              )}
            />
          </Stack>

          {isShared && (
            <Stack className={"card-panel"} gap={"lg"}>
              <Stack gap={"xs"}>
                <Title header={t("workflow:visibility_employee_preview")} size={"md"} />
                <Text className={"text-secondary"}>
                  {t("workflow:visibility_employee_preview_description")}
                </Text>
              </Stack>
              <Controller
                control={control}
                name={"emoji"}
                render={({ field: { value, onChange } }) => (
                  <EmojiPickerDropdown onEmojiSelect={(emoji) => onChange(emoji)}>
                    <Box className={"group relative h-16 w-16 cursor-pointer"}>
                      <Avatar
                        emoji={value}
                        size={"xl"}
                        className={"border border-dark/3 shadow-lg"}
                      />
                      <Box
                        className={
                          "absolute inset-0 flex items-center justify-center rounded-lg bg-dark/20 opacity-0 group-hover:opacity-100"
                        }
                      >
                        <FontAwesomeIcon
                          icon={"fa-light fa-icons"}
                          className={"text-2xl text-light"}
                        />
                      </Box>
                    </Box>
                  </EmojiPickerDropdown>
                )}
              />
              <Stack>
                <Input
                  type={"text"}
                  label={t("common:title")}
                  className={"[&_input]:font-semibold"}
                  {...register("public_title")}
                />
                <Textarea
                  label={t("common:description")}
                  className={"[&_textarea]:font-semibold"}
                  {...register("public_description")}
                />
              </Stack>
              <Stack gap={"sm"}>
                <Text className={"text-xs uppercase"}>{t("core:preview")}</Text>
                <ListItem
                  behavior={"card"}
                  className={"rounded-xs bg-dark/5 px-4 py-3"}
                  emoji={previewEmoji}
                  title={previewTitle || workflow.title}
                  caption={previewDescription || undefined}
                  forceShowAvatar={true}
                />
              </Stack>
            </Stack>
          )}

          <Button type={"submit"} size={"lg"} isLoading={workflowUpdateDetails.isPending}>
            {t("common:continue")}
          </Button>
        </Stack>
      </form>
    </FullScreenPage>
  );
};
